/**
 * "Divide into sections" UI state: which board's panel is open, the in-flight
 * request, and the accepted-or-not analysis the preview renders from. Local
 * view state only — applying the analysis is a doc mutation (apply.ts).
 *
 * Module SINGLETON: opened from the sections icon in the board's top-right
 * pill (BoardScreen), which sits outside the canvas tree the panel and
 * overlay mount in. The
 * analysis is also read by TWO mounts (the screen-space panel and the
 * in-camera highlight overlay), which a singleton serves without threading
 * props through BoardCanvas.
 */
import { createStore, type StoreApi } from "zustand/vanilla";

/** The board/sheet the panel is open for. */
export interface SectionsTarget {
  boardId: string;
  sheetId: string;
}

/** One suggested section, colored for the preview. */
export interface SectionPreview {
  title: string;
  /**
   * The section's dominant mood — a summary, NOT what the Mood column is
   * written from (that is `moods`); the fallback for rows no span covers.
   */
  mood: string;
  /** Preview highlight + applied cell fill — same hex for both. */
  color: string;
  /** Row ids in script order; possibly split across several places. */
  rowIds: readonly string[];
}

/**
 * One contiguous run of rows delivered in one mood (F-139). `rowIds[0]` is
 * the exact row this mood begins on — for every span after the first, the
 * sentence where the mood CHANGED.
 */
export interface MoodPreview {
  mood: string;
  /** Row ids in script order — one unbroken run. */
  rowIds: readonly string[];
}

/** A finished analysis, held until the user applies or cancels it. */
export interface SectionsAnalysis {
  sections: readonly SectionPreview[];
  /** Mood spans in script order; empty when the analysis carried no moods. */
  moods: readonly MoodPreview[];
  /** The model that actually analyzed — shown in the panel. */
  model: string;
}

export interface SectionsUiState {
  target: SectionsTarget | null;
  loading: boolean;
  error: string | null;
  analysis: SectionsAnalysis | null;
  /** Open (or re-target) the panel — always starts from a clean setup phase. */
  open(target: SectionsTarget): void;
  close(): void;
  setLoading(loading: boolean): void;
  setError(error: string | null): void;
  setAnalysis(analysis: SectionsAnalysis | null): void;
}

export type SectionsUiStore = StoreApi<SectionsUiState>;

export const sectionsUi: SectionsUiStore = createStore<SectionsUiState>((set) => ({
  target: null,
  loading: false,
  error: null,
  analysis: null,
  open: (target) => set({ target, loading: false, error: null, analysis: null }),
  close: () => set({ target: null, loading: false, error: null, analysis: null }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setAnalysis: (analysis) => set({ analysis }),
}));
